import * as THREE from "three";
import { TransformType } from "utils/interface.utils";
import { updateObjectInTargetImage, setIsUpdatingFromEditorWorkspace, selectObjectKeepingTool } from "store/reducers/editorSlice";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { applyTransformCursor } from "components/editor_workspace/scene/transform_cursor.helper";

// Drag Objects and update position:
export const dragObjectsAndUpdatePosition = (workspace: WorkspaceContext) => {
    const { rendererRef, canvasRef, sceneRef, cameraRef, targetImageRef, selectedTransformControlRef, selectedObjectIDRef, targetImageDataListRef, selectedTargetIndexRef } = workspace.refs;
    const raycaster = new THREE.Raycaster();
    // Click mouse position for vector2
    const clickMousePosition = new THREE.Vector2();
    // Last move mouse position for vector2
    const lastMoveMousePosition = new THREE.Vector2();
    let draggableObject: THREE.Object3D | null = null;
    let isDragging = false;

    const draggingObjectsSet = new Set<string>();

    const handleMouseDown = (event: MouseEvent) => {
        if (!canvasRef.current || !cameraRef.current || !sceneRef.current || !targetImageRef.current) return;

        // getBoundingClientRect() gives the position of the canvas in the browser window.
        const rect = canvasRef.current.getBoundingClientRect();

        // Calculate the mouse position in Normalized Device Coordinates(NDC) which is a range of -1 to 1 for both x and y and center of the screen is 0,0.
        // NDC is a camera-relative coordinate system, meaning it represents positions within the camera's view, not the screen size itself.
        // By using NDC, you don't have to worry about how large or small the screen is — interactions remain predictable based on the camera's field of view.
        clickMousePosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        clickMousePosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Set the raycaster to the camera
        raycaster.setFromCamera(clickMousePosition, cameraRef.current);

        // First, check for direct intersections
        const intersects = raycaster.intersectObjects(sceneRef.current.children, true);
        if (intersects.length > 0) {
            // Resolve the draggable object for each ray intersection, nearest first.
            const resolveDraggable = (object: THREE.Object3D): THREE.Object3D | null => {
                if (object.userData.isDraggable) return object;
                let parent = object.parent;
                while (parent) {
                    if (parent.userData.isDraggable) return parent;
                    parent = parent.parent;
                }
                return null;
            };

            // Prefer whichever draggable hit belongs to the currently selected object (the one
            // showing the transform box) over raw raycast distance. Two overlapping objects at
            // the same X/Y can hit in either order depending on their world Z, which doesn't
            // necessarily match visual stacking (renderOrder) or which one is actually selected —
            // without this, dragging the front/selected object could move the object behind it.
            let selectedDraggableObject: THREE.Object3D | null = null;
            let firstDraggableObject: THREE.Object3D | null = null;

            for (const intersect of intersects) {
                const candidate = resolveDraggable(intersect.object);
                if (!candidate) continue;

                // Of the unselected hits, the one drawn on top wins (highest renderOrder; the nearest
                // hit breaks a tie). Raycast order is z distance, which is not what the author sees.
                if (!firstDraggableObject || candidate.renderOrder > firstDraggableObject.renderOrder) {
                    firstDraggableObject = candidate;
                }

                if (selectedObjectIDRef.current && candidate.userData.id === selectedObjectIDRef.current) {
                    selectedDraggableObject = candidate;
                }
            }

            // The selected object wins its own overlap only while nothing drawn above it is under the
            // pointer too; otherwise the one on top is what the author sees and means to pick.
            if (selectedDraggableObject && firstDraggableObject && firstDraggableObject.renderOrder > selectedDraggableObject.renderOrder) {
                selectedDraggableObject = null;
            }

            draggableObject = selectedDraggableObject ?? firstDraggableObject;

            // If we found a draggable object, start dragging
            if (draggableObject) {
                // If you found a draggable object, start dragging by setting isDragging to true
                isDragging = true;
                applyTransformCursor(canvasRef.current, { tool: selectedTransformControlRef.current, corner: null, dragging: true });

                const objectId = draggableObject.userData.id;
                const targetImageObjects = targetImageDataListRef.current[selectedTargetIndexRef.current ?? 0]?.objects;
                const objectData = targetImageObjects?.find(obj => obj.id === objectId);

                // Pressing an object that is not the selected one selects it, with its box.
                if (objectData && objectId !== selectedObjectIDRef.current) {
                    workspace.dispatch(selectObjectKeepingTool({ objectID: objectId, objectType: objectData.type }));
                }

                // Synchronize the THREE.js object position with Redux data
                if (objectData) {
                    const { positionX, positionY, positionZ } = objectData.properties;
                    draggableObject.position.set(positionX, positionY, positionZ);
                }

                // Copy the click mouse position(where the mouse was clicked) to the last move mouse position(where the mouse was last moved) which is in NDC.
                lastMoveMousePosition.copy(clickMousePosition);

                draggingObjectsSet.add(objectId);

                // Add event listeners for mousemove and mouseup
                window.addEventListener("mousemove", handleMouseMove);
                window.addEventListener("mouseup", handleMouseUp);
            }
        }
    };

    // This function is called when the mouse is moved which is where the dragging happens.
    const handleMouseMove = (event: MouseEvent) => {
        if (!isDragging || !draggableObject || !canvasRef.current || !cameraRef.current || !targetImageRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();

        const mouseXPosition = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const mouseYPosition = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Calculate how much the mouse has moved since the last move event and that's why i used the name for the variable deltaX and deltaY. delta (Δ) is a common mathematical notation that represents a change in a value.
        const deltaX = mouseXPosition - lastMoveMousePosition.x; // Horizontal movement
        const deltaY = mouseYPosition - lastMoveMousePosition.y; // Vertical movement

        // Update the last move position
        lastMoveMousePosition.x = mouseXPosition;
        lastMoveMousePosition.y = mouseYPosition;

        // This is a sensitivity adjustment factor (currently set to 1, so it doesn't change anything) If set to 2, objects would move twice as far for the same mouse movement If set to 0.5, objects would move half as far
        const movementScale = 1;

        // Calculate the height of the camera's frustum in world units which is basically the scene's height.
        // In an orthographic camera, top and bottom define the vertical boundaries of the camera's view.
        const viewHeight = cameraRef.current.top - cameraRef.current.bottom;

        // This calculation ensures that the mouse movement which is in NDC is converted to world units.
        // The reason you need to convert the NDC to world units is because objects in the three.js scene are positioned in world units which is x,y,z coordinates.
        // Since the relationship between screen space and world space is linear in an orthographic projection, the conversion works by multiplying by the view height.
        // Multiplying the delta values by the view height ensures that the object moves in respect to the camera's view which is in world units.
        // The reason to divide the viewHeight by 2 is because the NDC ranges of 2 units from -1 to 1.
        // For example, if viewHeight is 10, then viewHeight / 2 is 5, meaning each NDC unit equals 5 world units
        const worldDeltaX = deltaX * (viewHeight / 2) * movementScale;
        const worldDeltaY = deltaY * (viewHeight / 2) * movementScale;

        if (selectedTransformControlRef.current === TransformType.POSITION) {
            draggableObject.position.x += worldDeltaX;
            draggableObject.position.y += worldDeltaY;

            let transformBox = draggableObject.userData.transformBox;
            if (transformBox) {
                transformBox.position.copy(draggableObject.position);
                transformBox.updateMatrix();
                transformBox.updateMatrixWorld(true);
            } else if (sceneRef.current) {
                const objectID = draggableObject.userData.id;
                sceneRef.current.traverse(child => {
                    if (child.userData && child.userData.associatedObjectID === objectID) {
                        transformBox = child;
                        // Store reference back to the draggableObject for future use
                        if (draggableObject) {
                            draggableObject.userData.transformBox = transformBox;
                            transformBox.position.copy(draggableObject.position);
                            transformBox.updateMatrix();
                            transformBox.updateMatrixWorld(true);
                        }
                    }
                });
            }

            // Update the object's position in the Redux store
            const objectId = draggableObject.userData.id;
            updateObjectPositionInStore(workspace, objectId, draggableObject.position.x, draggableObject.position.y, draggableObject.position.z);
        }

        if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
    }

    // When the mouse is released, stop dragging
    const handleMouseUp = (event: MouseEvent) => {
        if (draggableObject) {
            const objectId = draggableObject.userData.id;
            draggingObjectsSet.delete(objectId);
            // Final frame of the gesture — commits the whole drag as a single undo step.
            updateObjectPositionInStore(workspace, objectId, draggableObject.position.x, draggableObject.position.y, draggableObject.position.z, true);
        }

        isDragging = false;
        draggableObject = null;

        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);

        // Back to the active tool's resting cursor
        applyTransformCursor(canvasRef.current, { tool: selectedTransformControlRef.current, corner: null, dragging: false });
    }

    if (canvasRef.current) {
        // HandleMouseDown is called when the mouse is clicked.
        canvasRef.current.addEventListener('mousedown', handleMouseDown);
    }

    return  {
        cleanup: () => {
            if (canvasRef.current) {
                canvasRef.current.removeEventListener('mousedown', handleMouseDown);
            }
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        },

        isObjectBeingDraggedAndPositioned: (objectId: string) => draggingObjectsSet.has(objectId)
    };
};

export const updateObjectPositionInStore = (workspace: WorkspaceContext, objectId: string, x: number, y: number, z: number, commitHistory: boolean = false) => {
    const { targetImageDataListRef, selectedTargetIndexRef } = workspace.refs;
    const { dispatch } = workspace;
    const targetImageId = targetImageDataListRef.current[selectedTargetIndexRef.current ?? 0]?.id;

    if (!targetImageId || !objectId) return;

    // Find the object in the current state
    const currentObject = targetImageDataListRef.current[selectedTargetIndexRef.current ?? 0]?.objects.find(
        (obj) => obj.id === objectId
    );

    if (!currentObject) return;

    // Create a new object with updated position
    const updatedObject = {
        ...currentObject,
        properties: {
            ...currentObject.properties,
            positionX: Math.round(x),
            positionY: Math.round(y),
            positionZ: Math.round(z)
        }
    };
    // Mid-drag frames pass commitHistory:false; the final drag-end frame passes true so the
    // whole gesture collapses into a single undo step.
    dispatch(updateObjectInTargetImage({
        targetImageID: targetImageId,
        object: updatedObject,
        commitHistory
    }));
    dispatch(setIsUpdatingFromEditorWorkspace(true));
};
